import Chip from '@mui/material/Chip';
import dayjs from 'dayjs';
import Typography from '@mui/material/Typography'; // Import Typography for headings
import Box from '@mui/material/Box'; // Import Box for spacing

// Convert dayjs date to only time
const convertDayjsToTime = (date) => {
    return dayjs(date).format("HH:mm");
}

// Check if current time is between shift start and end
const isTimeBetween = (currentTime, startTime, endTime) => {
    // convert the currentTime to seconds
    const currentTimeInSeconds = currentTime.hour() * 3600 + currentTime.minute() * 60 + currentTime.second();
    const startTimeObj = dayjs(startTime);
    const endTimeObj = dayjs(endTime);
    const startTimeInSeconds = startTimeObj.hour() * 3600 + startTimeObj.minute() * 60 + startTimeObj.second();
    const endTimeInSeconds = endTimeObj.hour() * 3600 + endTimeObj.minute() * 60 + endTimeObj.second();

    // Handle shifts that cross midnight (end time is earlier than start time)
    if (endTimeInSeconds < startTimeInSeconds) {
        // Check if current time is after start OR before end
        return currentTimeInSeconds >= startTimeInSeconds || currentTimeInSeconds <= endTimeInSeconds;
    }

    // Normal case: shift is within the same day
    return currentTimeInSeconds >= startTimeInSeconds && currentTimeInSeconds <= endTimeInSeconds;
}

export default function ShiftsList({
    shifts,
    currentDay, // Keep currentDay prop for potential future use, though not directly used for filtering now
    selectedShift,
    handleShiftSelection
}) {

    const currentTime = dayjs();

    // Filter shifts into ongoing and completed
    const ongoingShifts = shifts.filter(shift => isTimeBetween(currentTime, shift.start, shift.end));
    const completedShifts = shifts.filter(shift => !isTimeBetween(currentTime, shift.start, shift.end));

    // Helper function to render a list of chips
    const renderShiftChips = (shiftList, isOngoing) => {
        return shiftList.map((shift, index) => (
            <Chip
                disabled={!isOngoing} // Disable completed shifts
                onClick={isOngoing ? () => handleShiftSelection(shift) : undefined} // Only ongoing shifts are clickable
                key={`${isOngoing ? 'ongoing' : 'completed'}-${shift._id || index}`} // Use shift._id if available for a more stable key
                label={`${shift.name} (${convertDayjsToTime(shift.start)} - ${convertDayjsToTime(shift.end)})`}
                variant="outlined"
                sx={{
                    m: 0.5, // Add some margin around chips
                    width: 'fit-content',
                    cursor: isOngoing ? 'pointer' : 'default',
                    borderColor: selectedShift?.[shift._id]
                        ? 'primary.main' // Use primary color border when selected
                        : isOngoing ? 'primary.main' : 'grey.500',
                    borderWidth: isOngoing ? 2 : 1,
                    backgroundColor: selectedShift?.[shift._id]
                        ? 'primary.light' // Use primary.light for selected background
                        : 'transparent',
                    color: selectedShift?.[shift._id]
                        ? 'primary.contrastText' // Ensure contrast text for selected
                        : isOngoing ? 'text.primary' : 'text.disabled',
                    '&:hover': isOngoing ? {
                        color: selectedShift?.[shift._id]
                            ? 'text.primary' // Ensure contrast text for selected
                            : isOngoing ? 'text.primary' : 'text.disabled',
                        backgroundColor: selectedShift?.[shift._id]
                            ? 'primary.main' // Change selected background to primary.main on hover
                            : 'action.hover', // Standard hover for non-selected
                        borderColor: 'primary.dark', // Darken border on hover for both selected and non-selected ongoing
                    } : {},
                }}
            />
        ));
    };


    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}> {/* Add overall container with spacing */}
            {/* Ongoing Shifts Section */}
            {ongoingShifts.length > 0 && (
                <Box>
                    <Typography variant="subtitle2" gutterBottom>Ongoing</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap' }}> {/* Container for chips */}
                        {renderShiftChips(ongoingShifts, true)}
                    </Box>
                </Box>
            )}

            {/* Completed Shifts Section */}
            {completedShifts.length > 0 && (
                <Box>
                    <Typography variant="subtitle2" gutterBottom>Completed</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap' }}> {/* Container for chips */}
                        {renderShiftChips(completedShifts, false)}
                    </Box>
                </Box>
            )}

            {/* Handle case where there are no shifts at all */}
            {shifts.length === 0 && (
                <Typography variant="body2" color="text.secondary">No shifts available.</Typography>
            )}
        </Box>
    );
}
